/**
 * Service layer — all backend HTTP communication lives here.
 * Uses relative URLs so requests always go to whichever host/port served the
 * page, which is exactly where the proxy lives.  No origin construction needed.
 */

const MAX_RETRIES = 2;
const RETRY_DELAYS_MS = [5000, 12000]; // 5s then 12s — slow LLMs need breathing room
const delay = (ms) => new Promise(res => setTimeout(res, ms));

// ── Session token ──────────────────────────────────────────────────────────
// Stored in localStorage on login; attached as a Bearer header to every gated
// endpoint (chat / tts / summarize / compact / stocks). The server only
// enforces it when IVA_AUTH_ENFORCED is on, so this is a no-op in demo mode.
const TOKEN_KEY = 'iva.token';
export function setAuthToken(token) {
  try { token ? localStorage.setItem(TOKEN_KEY, token) : localStorage.removeItem(TOKEN_KEY); } catch {}
}
export function getAuthToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
/** Headers object with the Bearer token merged in (if we have one). */
export function authHeaders(base = {}) {
  const t = getAuthToken();
  return t ? { ...base, Authorization: `Bearer ${t}` } : { ...base };
}

/**
 * Send a chat message to the unified backend.
 * Skips retry on 4xx (client errors that won't resolve on their own).
 *
 * @param {object} opts
 * @param {string}  opts.message     - User message text
 * @param {Array}   opts.history     - Conversation history array
 * @param {string}  opts.provider    - 'cohere' | 'openrouter' | 'claude' | 'ollama' | 'omlx'
 * @param {string}  [opts.model]     - Optional model override
 * @param {object}  [opts.profileData] - Optional profile context
 * @param {AbortSignal} [opts.signal]
 * @param {boolean} [opts.noThink]
 * @param {string}  [opts.customSystemPrompt] - Optional system prompt override
 * @param {string}  [opts.username]  - Logged-in username (for server-side usage logging)
 * @returns {Promise<{ text: string, model: string|null }>}
 */
/**
 * Change the logged-in user's password. Verifies the current password
 * server-side (per-user hash, or the shared password for accounts still on it).
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function changePassword({ username, currentPassword, newPassword }) {
  const res = await fetch('/api/auth/change-password', {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ username, currentPassword, newPassword }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    return { success: false, error: data.error || `Request failed (HTTP ${res.status}).` };
  }
  return { success: true };
}

/**
 * Self-serve "forgot password": ask an admin to reset this account. Response is
 * intentionally generic (never reveals whether the username exists).
 * @returns {Promise<{ success: boolean, message?: string, error?: string }>}
 */
export async function requestPasswordReset(username) {
  const res = await fetch('/api/auth/request-reset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    return { success: false, error: data.error || `Request failed (HTTP ${res.status}).` };
  }
  return data;
}

// ── Admin: user management (server gates these behind an admin token) ────────
async function adminPost(path, body) {
  const res = await fetch(path, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    return { success: false, error: data.error || `Request failed (HTTP ${res.status}).` };
  }
  return data;
}

/** List all accounts + orphan logins (people with no account yet). */
export async function adminListUsers() {
  const res = await fetch('/api/admin/users', { headers: authHeaders() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) {
    return { success: false, error: data.error || `Request failed (HTTP ${res.status}).` };
  }
  return data; // { success, accounts, orphans }
}

export const adminApproveUser = (username) => adminPost('/api/admin/users/approve', { username });
export const adminRevokeUser  = (username) => adminPost('/api/admin/users/revoke', { username });
export const adminCreateUser  = (username, password, role = 'guest') =>
  adminPost('/api/admin/users/create', { username, password, role });
export const adminResetPassword = (username, password) =>
  adminPost('/api/admin/users/reset-password', { username, password });
export const adminDismissReset = (username) => adminPost('/api/admin/users/dismiss-reset', { username });

/**
 * Load a profile to the server session cache.
 * Call once when a profile is selected; subsequent chat calls use sessionId + sessionSummary.
 * @returns {Promise<{ sessionId: string, sessionSummary: object }>}
 */
export async function loadProfile(profileData, existingSessionId = null) {
  const body = { profileData };
  if (existingSessionId) body.sessionId = existingSessionId;
  const res = await fetch('/api/profile/load', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  console.log('[loadProfile] status:', res.status, 'ok:', res.ok, 'content-type:', res.headers.get('content-type'));
  if (!res.ok) throw new Error(`Profile load failed: HTTP ${res.status}`);
  const data = await res.json();
  console.log('[loadProfile] response data:', data);
  return data;
}

/**
 * Clear the server-side persistent customer memory (learned concepts, category
 * corrections, preferences). The server re-bootstraps from the active session's
 * transactions so the merchant vocabulary survives the reset.
 * @returns {Promise<{ ok: boolean, existed: boolean, rebootstrapped: boolean }>}
 */
export async function clearCustomerMemory(customerId) {
  const res = await fetch(`/api/memory/${encodeURIComponent(customerId)}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(`Memory clear failed: HTTP ${res.status}`);
  return res.json();
}

/**
 * Fetch the full persistent customer-memory JSON (everything IVA has learned:
 * aboutMe, likes, dislikes, goals, concepts, categoryOverrides, interactionStyle,
 * understanding, interactionLog). For the dev console's MEMORY tab. Returns null
 * when no memory exists yet (404) or on any error — never throws.
 * @returns {Promise<object|null>}
 */
export async function getMemory(customerId) {
  if (!customerId) return null;
  try {
    const res = await fetch(`/api/memory/${encodeURIComponent(customerId)}`);
    if (!res.ok) return null;
    return await res.json();
  } catch (_) {
    return null;
  }
}

/**
 * Memory write path — send a completed exchange to the server's extraction agent
 * so durable facts (likes, goals, concept definitions, style preferences) are
 * merged into the customer's persistent memory. Fire-and-forget: never throws.
 * @returns {Promise<{ ok: boolean, changes?: string[] }|null>}
 */
export async function extractMemory(sessionId, question, answer, customerId) {
  try {
    const res = await fetch('/api/memory/extract', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // customerId lets the server still extract after the 30-min session cache expires.
      body: JSON.stringify({ sessionId, question, answer, customerId }),
    });
    return res.ok ? await res.json() : null;
  } catch (_) {
    return null;
  }
}

/**
 * Proactive confirmation — given the customer's message, ask the server which
 * unconfirmed assumptions (guesses IVA derived about them) are topic-relevant, so
 * the UI can surface a tap-to-confirm card. Fire-and-forget: never throws.
 * @returns {Promise<Array<{id:string,text:string,basis:string,confidence:string}>>}
 */
export async function getRelevantAssumptions(customerId, message) {
  if (!customerId || !message) return [];
  try {
    const res = await fetch('/api/memory/assumptions/relevant', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, message }),
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.prompts) ? data.prompts : [];
  } catch (_) {
    return [];
  }
}

/**
 * Record the customer's decision on a derived assumption. 'confirm' promotes it to
 * a stated fact; 'dismiss' retires it. Returns the server result (or null on error).
 * @returns {Promise<{ ok:boolean, decision?:string, fact?:string|null }|null>}
 */
export async function decideAssumption(customerId, id, decision) {
  try {
    const res = await fetch('/api/memory/assumption', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, id, decision }),
    });
    return res.ok ? await res.json() : null;
  } catch (_) {
    return null;
  }
}

/**
 * Ask the filter agent to extract query-relevant transactions from the cached profile.
 * Falls back gracefully — always returns { filteredTransactions: [], skipped: true } on error.
 * @returns {Promise<{ filteredTransactions: object[], skipped: boolean }>}
 */
export async function filterProfile(sessionId, question, history = []) {
  try {
    const res = await fetch('/api/profile/filter', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, question, history }),
    });
    if (!res.ok) return { filteredTransactions: [], skipped: true };
    return res.json();
  } catch {
    return { filteredTransactions: [], skipped: true };
  }
}

export async function sendChatMessage({ message, history = [], provider = 'cohere', model = null, profileData = null, signal = null, noThink = false, customSystemPrompt = null, username = null, sessionId = null, sessionSummary = null, filteredTransactions = [], filteredStats = null }) {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      if (signal?.aborted) throw new Error('AbortError');
      console.log(`[BackendAPI] POST /api/chat  provider=${provider}  model=${model}  (attempt ${attempt + 1})`);

      const body = { message, history, provider };
      if (model)                        body.model = model;
      if (profileData)                  body.profileData = profileData;
      if (noThink)                      body.noThink = true;
      if (customSystemPrompt)           body.customSystemPrompt = customSystemPrompt;
      if (username)                     body.username = username;
      if (sessionId)                    body.sessionId = sessionId;
      if (sessionSummary)               body.sessionSummary = sessionSummary;
      if (filteredTransactions?.length) body.filteredTransactions = filteredTransactions;
      if (filteredStats)               body.filteredStats = filteredStats;

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`API error ${response.status}:`, errorData);
        if (response.status >= 400 && response.status < 500) {
          throw new Error(`HTTP ${response.status}: ${errorData.error || 'Request failed'}`);
        }
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      if (!data.reply) throw new Error('Empty reply from server');
      console.log('[BackendAPI] reply:', data.reply.slice(0, 100), '... model:', data.model || 'unknown');
      return { text: data.reply, model: data.model || null };
    } catch (error) {
      console.error(`Attempt ${attempt + 1} failed:`, error.message);
      const isClientError = error.message.startsWith('HTTP 4');
      const isAbort = error.name === 'AbortError' || error.message === 'AbortError';
      if (attempt < MAX_RETRIES && !isClientError && !isAbort) {
        console.log(`Retrying in ${RETRY_DELAYS_MS[attempt]}ms...`);
        await delay(RETRY_DELAYS_MS[attempt]);
      } else {
        throw error;
      }
    }
  }
}

/**
 * Fetch pre-computed background analysis results for a session.
 * Returns { ready: false } while agents are still running.
 * @param {string} sessionId
 * @returns {Promise<{ ready: boolean, anomalies?, subscriptions?, budgetPulse?, narrative? }>}
 */
export async function fetchInsights(sessionId) {
  try {
    const res = await fetch(`/api/profile/insights/${encodeURIComponent(sessionId)}`);
    if (!res.ok) return { ready: false };
    return res.json();
  } catch {
    return { ready: false };
  }
}

/**
 * Fetch the cashflow wellness assessment for a session (security→stability→
 * fulfillment framework). Computed deterministically server-side and cached per
 * session, so this is fast on repeat opens.
 * @param {string} sessionId
 * @returns {Promise<{ ready: boolean, stage?, cashflow?, assessment?, nextSteps? }>}
 */
export async function fetchCashflow(sessionId, asOf = null) {
  try {
    const qs = asOf ? `?asOf=${encodeURIComponent(asOf)}` : '';
    const res = await fetch(`/api/profile/cashflow/${encodeURIComponent(sessionId)}${qs}`);
    if (!res.ok) return { ready: false };
    return res.json();
  } catch {
    return { ready: false };
  }
}

/**
 * Fetch the "Today" feed for a session — the aggregated, priority-sorted list of
 * situations IVA noticed today (Day in the Life foundation).
 * @param {string} sessionId
 * @returns {Promise<{ ready: boolean, feed?: Array }>}
 */
export async function fetchTodayFeed(sessionId, asOf = null) {
  try {
    const qs = asOf ? `?asOf=${encodeURIComponent(asOf)}` : '';
    const res = await fetch(`/api/profile/today/${encodeURIComponent(sessionId)}${qs}`);
    if (!res.ok) return { ready: false };
    return res.json();
  } catch {
    return { ready: false };
  }
}

/**
 * Fetch the scenario-runner bootstrap for a session — the timeline (sorted step
 * dates + per-date counts + total) and the notable milestones to call out as the
 * simulated clock passes them. The moving views come from fetchBudgetOverview /
 * fetchTodayFeed with an asOf date.
 * @param {string} sessionId
 * @returns {Promise<{ ready: boolean, timeline?: object, milestones?: Array }>}
 */
export async function fetchScenario(sessionId) {
  try {
    const res = await fetch(`/api/profile/scenario/${encodeURIComponent(sessionId)}`);
    if (!res.ok) return { ready: false };
    return res.json();
  } catch {
    return { ready: false };
  }
}

/**
 * Fetch the budgeting overview for a session — the month-at-a-glance view: money
 * in vs. out, remaining to spend, upcoming bills, and per-category pace.
 * @param {string} sessionId
 * @returns {Promise<{ ready: boolean, overview?: object }>}
 */
export async function fetchBudgetOverview(sessionId, asOf = null) {
  try {
    const qs = asOf ? `?asOf=${encodeURIComponent(asOf)}` : '';
    const res = await fetch(`/api/profile/budget/${encodeURIComponent(sessionId)}${qs}`);
    if (!res.ok) return { ready: false };
    return res.json();
  } catch {
    return { ready: false };
  }
}

/**
 * Post a budget-config event (the customer steering their own budget): set/clear a
 * category limit, set the total monthly budget, recategorize / ignore / reclassify a
 * category or txn, or set a guardrail-alert threshold. Returns the freshly-derived
 * overview so the caller can reconcile its optimistic update. Undo = post the inverse.
 * @param {string} sessionId
 * @param {object} event - { type, ...fields }
 * @returns {Promise<{ ok: boolean, overview?: object }>}
 */
export async function postBudgetConfig(sessionId, event) {
  try {
    const res = await fetch(`/api/profile/budget/${encodeURIComponent(sessionId)}/config`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
    });
    if (!res.ok) return { ok: false };
    return res.json();
  } catch {
    return { ok: false };
  }
}

/**
 * Fetch the cumulative "smart savings" tally for a session — dollars saved, CO₂
 * saved, points, and streak from the customer's logged smarter choices (plan 250).
 * @param {string} sessionId
 * @returns {Promise<{ ready: boolean, total?: object }>}
 */
export async function fetchSmartSavings(sessionId) {
  try {
    const res = await fetch(`/api/profile/smart-savings/${encodeURIComponent(sessionId)}`);
    if (!res.ok) return { ready: false };
    return res.json();
  } catch {
    return { ready: false };
  }
}

/**
 * Fetch card protection status for a session — whether the card is frozen (from a
 * handle_fraud remediation) plus open disputes/notifications (plan 270).
 * @param {string} sessionId
 * @returns {Promise<{ ready: boolean, frozen?: boolean, card?: string, disputes?: object[] }>}
 */
export async function fetchCardStatus(sessionId) {
  try {
    const res = await fetch(`/api/profile/card-status/${encodeURIComponent(sessionId)}`);
    if (!res.ok) return { ready: false };
    return res.json();
  } catch {
    return { ready: false };
  }
}

/**
 * Check a pasted message for classic scam patterns (gift-card/urgent-transfer/bank
 * impersonation). Deterministic; no side effects (plan 270).
 * @param {string} text
 * @returns {Promise<{ risk: string, flagged: boolean, signals: object[], guidance: ?string }>}
 */
export async function checkScamMessage(text) {
  try {
    const res = await fetch('/api/fraud/scam-check', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: text || '' }),
    });
    if (!res.ok) return { risk: 'none', flagged: false, signals: [] };
    return res.json();
  } catch {
    return { risk: 'none', flagged: false, signals: [] };
  }
}

/**
 * Set the opt-in "auto-cover shortfalls" preference for this session's customer.
 * Persisted server-side so it survives reloads; the cover move itself stays
 * confirm-gated. @returns {Promise<{ ok: boolean, autoCoverShortfalls?: boolean }>}
 */
export async function setAutoCoverShortfalls(sessionId, enabled) {
  try {
    const res = await fetch(`/api/profile/auto-cover/${encodeURIComponent(sessionId)}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled: !!enabled }),
    });
    return await res.json();
  } catch {
    return { ok: false };
  }
}

/**
 * Preview a confirm-gated action (e.g. pay_down_credit_card) — returns what would
 * change (before/after + summary) WITHOUT applying anything. { ok:false, reason } on error.
 */
export async function previewAction(sessionId, type, params) {
  try {
    const res = await fetch('/api/action/preview', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, type, params: params || {} }),
    });
    return await res.json();
  } catch (e) {
    return { ok: false, reason: 'network error' };
  }
}

/**
 * Execute a confirm-gated action: applies it to the working copy, records it in
 * the customer's ledger, and invalidates the cashflow cache so it re-rolls.
 */
export async function executeAction(sessionId, type, params) {
  try {
    const res = await fetch('/api/action/execute', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, type, params: params || {} }),
    });
    return await res.json();
  } catch (e) {
    return { ok: false, reason: 'network error' };
  }
}

export async function queueAgentEMT(sessionId, { recipientEmail, amount, message } = {}) {
  try {
    const res = await fetch('/api/actions/emt/queue-agent', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, recipientEmail, amount, message }),
    });
    return await res.json();
  } catch (e) {
    return { ok: false, reason: 'network error' };
  }
}

/**
 * Manually trigger selected insight agents for an existing session.
 * The server resets insights to not-ready; client should resume polling fetchInsights.
 * @param {string} sessionId
 * @param {string[]} agents - subset of ['anomalies','subscriptions','budgetPulse','narrative']
 * @returns {Promise<{ ok: boolean }>}
 */
export async function runInsightsAgents(sessionId, agents) {
  try {
    const res = await fetch('/api/profile/insights/run', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId, agents }),
    });
    // 404 = the server dropped this session (30-min idle TTL). Surface the reason
    // so callers can transparently re-load the profile instead of failing silently.
    if (res.status === 404) return { ok: false, reason: 'session_not_found' };
    if (!res.ok) return { ok: false };
    return res.json();
  } catch {
    return { ok: false };
  }
}

/**
 * Rebuild the lightweight profile payload that /api/profile/load expects from the
 * client's loadedProfileData. Mirrors ChatScreen's chatProfilePayload memo. Returns
 * null for the built-in 'default' source (its transactionsData isn't reconstructable
 * here) — callers fall back to asking the user to reopen the profile.
 */
export function rebuildProfilePayload(loadedProfileData) {
  if (!loadedProfileData) return null;
  if (loadedProfileData.source === 'default') return null;
  if (!loadedProfileData.profile) return null;
  return { source: loadedProfileData.source, profile: loadedProfileData.profile };
}

/**
 * Re-create the server-side session under the SAME sessionId after a 30-min idle
 * eviction. The load route accepts an existing id and re-kicks the background
 * analysis automatically, so a successful revive also restarts the agents.
 * @returns {Promise<boolean>} true if the session is live again, false if it
 *   couldn't be rebuilt (no profile data, or the built-in default).
 */
export async function reviveSession(sessionId, loadedProfileData) {
  const payload = rebuildProfilePayload(loadedProfileData);
  if (!payload) return false;
  try {
    const res = await loadProfile(payload, sessionId);
    return !!res?.sessionId;
  } catch {
    return false;
  }
}

/**
 * Ask the server to LLM-summarize older conversation turns for context compaction.
 * Returns { summary: string } or throws on error.
 * Only called when COMPACTION_STRATEGY === 'llm' in ChatScreen.
 */
export async function compactHistory(history, provider, model) {
  const res = await fetch('/api/compact', {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ history, provider, model }),
  });
  if (!res.ok) throw new Error(`Compact failed: HTTP ${res.status}`);
  return res.json();
}

/**
 * Fetch the active model name for a given provider.
 *
 * @param {string} provider - 'cohere' | 'openrouter' | 'claude' | 'ollama' | 'omlx'
 * @returns {Promise<string|null>}
 */
export async function fetchModelConfig(provider) {
  try {
    const res = await fetch(`/api/config?provider=${encodeURIComponent(provider)}`);
    const data = await res.json();
    return data.model || null;
  } catch {
    return null;
  }
}

/**
 * Request TTS audio from the backend.
 * Returns a Blob (audio/mpeg) or throws on error.
 *
 * @param {object} opts
 * @param {string}  opts.provider - 'elevenlabs' | 'openai'
 * @param {string}  opts.text     - Text to synthesize
 * @param {string}  [opts.voiceId] - ElevenLabs voice ID
 * @param {string}  [opts.voice]   - OpenAI voice name
 * @returns {Promise<Blob>}
 */
export async function fetchInvestingAnalysis(sessionId) {
  try {
    const res = await fetch(`/api/investing/analysis/${sessionId}`, { headers: authHeaders() });
    return res.ok ? res.json() : null;
  } catch (_) { return null; }
}

export async function fetchRiskQuestions() {
  try {
    const res = await fetch('/api/investing/risk-questions', { headers: authHeaders() });
    return res.ok ? res.json() : { questions: [] };
  } catch (_) { return { questions: [] }; }
}

export async function saveRiskProfile(customerId, answers) {
  try {
    const res = await fetch('/api/investing/risk-profile', {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ customerId, answers }),
    });
    return res.ok ? res.json() : null;
  } catch (_) { return null; }
}

export async function getRiskProfile(customerId) {
  if (!customerId) return null;
  try {
    const res = await fetch(`/api/investing/risk-profile/${customerId}`, { headers: authHeaders() });
    return res.ok ? res.json() : null;
  } catch (_) { return null; }
}

export async function planGoal(goal, assumptions) {
  try {
    const res = await fetch('/api/investing/plan', {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ goal, assumptions }),
    });
    return res.ok ? res.json() : null;
  } catch (_) { return null; }
}

export async function synthesizeSpeech({ provider, text, voiceId, voice }) {
  const body = { text, provider };
  if (voiceId) body.voiceId = voiceId;
  if (voice)   body.voice   = voice;

  const res = await fetch('/api/tts', {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }

  return res.blob();
}

export async function getSwipePreferences(customerId) {
  if (!customerId) return null;
  try {
    const res = await fetch(`/api/swipe/preferences?customerId=${encodeURIComponent(customerId)}`, { headers: authHeaders() });
    if (!res.ok) return null;
    const data = await res.json();
    return data.swipePreferences || null;
  } catch (_) { return null; }
}

export async function saveSwipeBooking(customerId, payload) {
  try {
    const res = await fetch('/api/swipe/booking', {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ customerId, ...payload }),
    });
    return res.ok ? res.json() : { ok: false };
  } catch (_) { return { ok: false }; }
}

export async function submitTesterFeedback(text) {
  try {
    const res = await fetch('/api/tester-feedback', {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ text }),
    });
    return res.ok ? res.json() : { success: false, error: res.statusText };
  } catch (_) { return { success: false, error: 'network error' }; }
}

export async function searchFlights({ from, to, date, returnDate, currency = 'CAD' }) {
  try {
    const params = new URLSearchParams({ from, to, date, currency });
    if (returnDate) params.set('returnDate', returnDate);
    const res = await fetch(`/api/flights?${params}`);
    if (!res.ok) return { flights: [], mock: true, error: res.statusText };
    return res.json();
  } catch (e) {
    return { flights: [], mock: true, error: e.message };
  }
}

export function streamEMTAgent({ recipientName, recipientEmail, amount, note }, onStep, onDone) {
  fetch('/api/emt/agent', {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ recipientName, recipientEmail, amount, note }),
    credentials: 'include',
  }).then(res => {
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    const read = () => reader.read().then(({ done, value }) => {
      if (done) return;
      const chunk = decoder.decode(value);
      for (const line of chunk.split('\n')) {
        if (!line.startsWith('data: ')) continue;
        try {
          const evt = JSON.parse(line.slice(6));
          if (evt.type === 'step') onStep(evt);
          if (evt.type === 'done') onDone(evt);
        } catch (_) {}
      }
      read();
    });
    read();
  }).catch(err => onDone({ success: false, message: err.message }));
}
